import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router';

import EntityFormModal from '@sharedCR/common/EntityFormModal';
import { ModalSize } from '@sharedCR/coreDesignSystem/Modals';
import { LoansPaths } from 'app/configs/routesConfig/loans';
import _ from 'lodash';

import { AttendantCompanyTypeEnum, LoanApplicationTabRouteParam } from 'constants/loanApplication';
import useLoanId from 'hooks/useLoanId';
import useReleaseMyFieldLocks from 'hooks/useReleaseMyFieldLocks';
import useRevertFieldValues from 'hooks/useRevertFieldValues';

import { closeDialogAction, openDialogAction } from 'store/global/actions';
import {
  selectClosingCompanies,
  selectClosingInfo,
  selectEntityChangedPropsById,
  selectHiddenFieldIds,
  selectHiddenPageIds,
  selectIsChattelLoanFromPropertyAndLoanSummary,
  selectIsCurrentUserCanChangeLoan,
  selectIsUnsecuredFromPropertyAndLoanSummary,
  selectLoanClosingChangedPropsById,
  selectTrustInfo,
} from 'store/loanApplications/selectors';
import { clearLocalEditedFieldsAction } from 'store/presence/actions';
import { selectLocalEditedFieldPaths } from 'store/presence/selectors';

import ClosingCompanyModalContent from './components/ClosingCompanyModalContent';
import Content from './Content';
import { prefixChanges } from './Content/Content.utils';
import {
  calculateSectionPendingChangesCounts,
  getHeadTabs,
  getNavigationTabs,
  mapTrustPendingChanges,
  resolveActiveSection,
  SECTION_HREF_MAP,
} from './EditDrawer.config';

const EditDrawer = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { search } = useLocation();
  const { applicationId, tab, subtab: section } = useParams();
  const loanId = useLoanId();
  const revertFieldValues = useRevertFieldValues();
  const releaseMyFieldLocks = useReleaseMyFieldLocks();

  const localEditedFieldPaths = useSelector(selectLocalEditedFieldPaths);
  const isCurrentUserCanChangeLoan = useSelector(selectIsCurrentUserCanChangeLoan);
  const isUnsecured = useSelector(selectIsUnsecuredFromPropertyAndLoanSummary);
  const isChattel = useSelector(selectIsChattelLoanFromPropertyAndLoanSummary);
  const hiddenPageIds = useSelector(selectHiddenPageIds);
  const hiddenFieldIds = useSelector(selectHiddenFieldIds);
  const closingInfo = useSelector(selectClosingInfo);
  const closingCompanies = useSelector(selectClosingCompanies);
  const loanPendingChangesData = useSelector(
    selectLoanClosingChangedPropsById({
      loanClosingId: closingInfo?.loanClosingId,
      loanId,
      applicationId,
    }),
  );

  const titleCompany = useMemo(
    () => closingCompanies?.find(c => c.closingCompanyType === AttendantCompanyTypeEnum.TITLE),
    [closingCompanies],
  );

  const escrowCompany = useMemo(
    () => closingCompanies?.find(c => c.closingCompanyType === AttendantCompanyTypeEnum.ESCROW),
    [closingCompanies],
  );

  const titleCompanyPendingChanges = useSelector(
    selectEntityChangedPropsById(`${closingInfo?.loanClosingId}_${AttendantCompanyTypeEnum.TITLE}`),
  );
  const escrowCompanyPendingChanges = useSelector(
    selectEntityChangedPropsById(
      `${closingInfo?.loanClosingId}_${AttendantCompanyTypeEnum.ESCROW}`,
    ),
  );
  const titleLocationPendingChanges = useSelector(
    selectEntityChangedPropsById(titleCompany?.closingCompanyLocationId),
  );
  const escrowLocationPendingChanges = useSelector(
    selectEntityChangedPropsById(escrowCompany?.closingCompanyLocationId),
  );

  const sellerAttorney = useMemo(
    () =>
      closingCompanies?.find(
        c => c.closingCompanyType === AttendantCompanyTypeEnum.SELLER_ATTORNEY,
      ),
    [closingCompanies],
  );
  const buyerAttorney = useMemo(
    () =>
      closingCompanies?.find(c => c.closingCompanyType === AttendantCompanyTypeEnum.BUYER_ATTORNEY),
    [closingCompanies],
  );

  const sellerAttorneyPendingChanges = useSelector(
    selectEntityChangedPropsById(
      `${closingInfo?.loanClosingId}_${AttendantCompanyTypeEnum.SELLER_ATTORNEY}`,
    ),
  );
  const buyerAttorneyPendingChanges = useSelector(
    selectEntityChangedPropsById(
      `${closingInfo?.loanClosingId}_${AttendantCompanyTypeEnum.BUYER_ATTORNEY}`,
    ),
  );
  const sellerAttorneyLocationPendingChanges = useSelector(
    selectEntityChangedPropsById(sellerAttorney?.closingCompanyLocationId),
  );
  const buyerAttorneyLocationPendingChanges = useSelector(
    selectEntityChangedPropsById(buyerAttorney?.closingCompanyLocationId),
  );

  const trustInfo = useSelector(selectTrustInfo);
  const trustPendingChanges = useSelector(selectEntityChangedPropsById(trustInfo?.id));

  const pendingChangesData = useMemo(
    () => ({
      ...loanPendingChangesData,
      ...mapTrustPendingChanges(trustPendingChanges),
      ...prefixChanges(titleCompanyPendingChanges, 'title'),
      ...prefixChanges(titleLocationPendingChanges, 'title'),
      ...prefixChanges(escrowCompanyPendingChanges, 'escrow'),
      ...prefixChanges(escrowLocationPendingChanges, 'escrow'),
      ...prefixChanges(sellerAttorneyPendingChanges, 'sellerAttorney'),
      ...prefixChanges(sellerAttorneyLocationPendingChanges, 'sellerAttorney'),
      ...prefixChanges(buyerAttorneyPendingChanges, 'buyerAttorney'),
      ...prefixChanges(buyerAttorneyLocationPendingChanges, 'buyerAttorney'),
    }),
    [
      loanPendingChangesData,
      trustPendingChanges,
      titleCompanyPendingChanges,
      escrowCompanyPendingChanges,
      titleLocationPendingChanges,
      escrowLocationPendingChanges,
      sellerAttorneyPendingChanges,
      sellerAttorneyLocationPendingChanges,
      buyerAttorneyPendingChanges,
      buyerAttorneyLocationPendingChanges,
    ],
  );

  const isOpen = tab === LoanApplicationTabRouteParam.CLOSING_NEW && !!section;
  const currentSection = resolveActiveSection({ section, isChattel });

  const isValidSection = Object.values(SECTION_HREF_MAP).includes(currentSection);

  const [, setPendingChanges] = useState({});
  const [formPendingChangesCounts, setFormPendingChangesCounts] = useState({});
  const [invalidSections, setInvalidSections] = useState([]);

  const backendPendingChangesCounts = useMemo(
    () => calculateSectionPendingChangesCounts(pendingChangesData),
    [pendingChangesData],
  );

  const pendingChangesCounts = useMemo(() => {
    const merged = { ...backendPendingChangesCounts };
    Object.entries(formPendingChangesCounts).forEach(([sectionKey, count]) => {
      merged[sectionKey] = count;
    });

    return merged;
  }, [backendPendingChangesCounts, formPendingChangesCounts]);

  const onCountPendingChanges = useCallback(changes => {
    setFormPendingChangesCounts(calculateSectionPendingChangesCounts(changes));
  }, []);

  const onCloseDialog = useCallback(() => {
    dispatch(closeDialogAction());
  }, [dispatch]);

  const onOpenCompanyModal = useCallback(
    ({ closingCompany, header }) => {
      if (!isCurrentUserCanChangeLoan) {
        return;
      }

      dispatch(
        openDialogAction({
          header,
          isRestyled: true,
          size: ModalSize.XL,
          withDivider: true,
          hasScrollY: true,
          body: () => (
            <ClosingCompanyModalContent
              applicationId={applicationId}
              closingCompany={closingCompany}
              onClose={onCloseDialog}
            />
          ),
        }),
      );
    },
    [dispatch, isCurrentUserCanChangeLoan, applicationId, onCloseDialog],
  );

  const onClose = useCallback(
    actionParams => {
      if (actionParams?.openCompanyModal) {
        const company = actionParams.openCompanyModal;
        onOpenCompanyModal({
          closingCompany: company,
          header: `Edit ${company.closingCompanyType} Company`,
        });

        return;
      }

      if (localEditedFieldPaths.length) {
        releaseMyFieldLocks();
        revertFieldValues(localEditedFieldPaths);
        dispatch(clearLocalEditedFieldsAction());
      }

      const path = LoansPaths.applicationManagement.generatePath({
        applicationId,
        tab,
      });
      setPendingChanges({});
      setFormPendingChangesCounts({});
      history.push({ pathname: path, search });
    },
    [
      applicationId,
      tab,
      history,
      search,
      localEditedFieldPaths,
      revertFieldValues,
      releaseMyFieldLocks,
      dispatch,
      onOpenCompanyModal,
    ],
  );

  const additionalProps = useMemo(
    () => ({
      onCountPendingChanges,
      setPendingChanges,
      setInvalidSections,
    }),
    [onCountPendingChanges],
  );

  const navigationTabs = useMemo(
    () =>
      getNavigationTabs({
        applicationId,
        tab,
        history,
        search,
        pendingChangesCounts,
        invalidSections,
        hiddenPageIds,
        hiddenFieldIds,
        isUnsecured,
        isChattel,
      }),
    [
      applicationId,
      tab,
      history,
      search,
      pendingChangesCounts,
      invalidSections,
      hiddenPageIds,
      hiddenFieldIds,
      isUnsecured,
      isChattel,
    ],
  );

  const totalPendingChangesCount = useMemo(
    () => _.sum(Object.values(pendingChangesCounts)),
    [pendingChangesCounts],
  );

  const headTabs = useMemo(
    () => getHeadTabs({ pendingChangesCount: totalPendingChangesCount, isUnsecured }),
    [totalPendingChangesCount, isUnsecured],
  );

  if (!isOpen || !isValidSection) {
    return null;
  }

  return (
    <EntityFormModal
      isData
      Content={Content}
      additionalProps={additionalProps}
      headTabs={headTabs}
      headValue={headTabs[0]?.value}
      isOpen={isOpen}
      navigationTabs={navigationTabs}
      navigationValue={currentSection}
      title={isUnsecured ? 'Funding' : 'Closing'}
      onClose={onClose}
    />
  );
};

export default memo(EditDrawer);
